import { create } from 'zustand';
import { createStore } from 'zustand/vanilla';
import type { DashboardConfig, ChartConfig } from '../types/dashboard';
import { mockDashboardConfig } from '../data/mockDashboard';

export interface DashboardStore {
  dashboardConfig: DashboardConfig;

  setDashboardConfig: (c: DashboardConfig) => void;
  addChart: (chart: ChartConfig) => void;
  updateChart: (id: string, patch: Partial<ChartConfig>) => void;
  removeChart: (id: string) => void;
  moveChart: (from: number, to: number) => void;
}

export type DashboardStoreApi = ReturnType<typeof createDashboardStore>;

const storeActions = (
  set: (fn: (state: DashboardStore) => Partial<DashboardStore>) => void
): Omit<DashboardStore, 'dashboardConfig'> => ({
  setDashboardConfig: (c: DashboardConfig) => set(() => ({ dashboardConfig: c })),

  addChart: (chart: ChartConfig) =>
    set((state) => ({
      dashboardConfig: {
        ...state.dashboardConfig,
        charts: [...state.dashboardConfig.charts, chart],
      },
    })),

  updateChart: (id, patch) =>
    set((state) => ({
      dashboardConfig: {
        ...state.dashboardConfig,
        charts: state.dashboardConfig.charts.map((ch) => (ch.id === id ? { ...ch, ...patch } : ch)),
      },
    })),

  removeChart: (id) =>
    set((state) => ({
      dashboardConfig: {
        ...state.dashboardConfig,
        charts: state.dashboardConfig.charts.filter((ch) => ch.id !== id),
      },
    })),

  moveChart: (from, to) =>
    set((state) => {
      const next = [...state.dashboardConfig.charts];
      const [item] = next.splice(from, 1);
      next.splice(to, 0, item);
      return { dashboardConfig: { ...state.dashboardConfig, charts: next } };
    }),
});

export function createDashboardStore(initialConfig?: DashboardConfig) {
  return createStore<DashboardStore>((set) => ({
    dashboardConfig: initialConfig ?? mockDashboardConfig,
    ...storeActions(set),
  }));
}

export const useDashboardStore = create<DashboardStore>((set) => ({
  dashboardConfig: mockDashboardConfig,
  ...storeActions(set),
}));
